import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { UilCalendarAlt, UilArrowRight } from "@iconscout/react-unicons";
import { useDispatch } from "react-redux";
import { getPostByType } from "../../redux/blog/blogSlice";
import urlConfig from "../../helper/urlConfig";

/**
 * Configuration constants
 * Following Open/Closed Principle - easy to modify without changing component logic
 */
const CONFIG = {
  MAX_NEWS_ITEMS: 6,
  TITLE_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 150,
  PLACEHOLDER_IMAGE: "https://via.placeholder.com/128",
};

/**
 * Utility functions
 * Single Responsibility - each function does one thing
 */
const truncateText = (text, maxLength) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const getImageUrl = (item) => {
  if (item.images?.length > 0 && item.images[0].upload_path) {
    return `${urlConfig.baseUrl}/${item.images[0].upload_path}`;
  }
  return item.img || CONFIG.PLACEHOLDER_IMAGE;
};

const getItemUrl = (item) => item.url || `/news-events/${item.id}`;

const isExternalUrl = (url) => url?.startsWith('http://') || url?.startsWith('https://');

/**
 * LoadingSpinner Component
 * Single Responsibility - handles loading state display
 */
const LoadingSpinner = () => (
  <div className="p-8 sm:p-12 flex justify-center items-center">
    <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 
                    border-4 border-indigo-200 border-t-indigo-600" />
  </div>
);

/**
 * NewsItemImage Component
 * Single Responsibility - handles image rendering with fallback
 */
const NewsItemImage = ({ src, alt }) => (
  <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 
                  flex-shrink-0 rounded-lg overflow-hidden shadow-md border border-gray-200">
    <img
      src={src}
      alt={alt}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = CONFIG.PLACEHOLDER_IMAGE;
      }}
    />
  </div>
);

/**
 * NewsItemContent Component
 * Single Responsibility - handles content rendering
 */
const NewsItemContent = ({ title, description, date }) => (
  <div className="flex-1 min-w-0">
    <h3 className="font-semibold text-gray-800 text-sm sm:text-base lg:text-lg 
                   line-clamp-2 group-hover:text-indigo-600 transition-colors mb-1 sm:mb-2">
      {truncateText(title, CONFIG.TITLE_MAX_LENGTH)}
    </h3>
    {date && (
      <div className="flex items-center gap-1 text-gray-500 text-xs sm:text-sm mb-2">
        <UilCalendarAlt size={14} />
        <span>{formatDate(date)}</span>
      </div>
    )}
    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed line-clamp-2 sm:line-clamp-3">
      {truncateText(description, CONFIG.DESCRIPTION_MAX_LENGTH)}
    </p>
  </div>
);

/**
 * ReadMoreLink Component
 * Single Responsibility - handles the read more link/button
 */
const ReadMoreLink = ({ url, isExternal }) => {
  const linkClasses = `
    text-xs sm:text-sm font-medium text-indigo-600 hover:text-indigo-800 
    flex items-center transition-colors whitespace-nowrap
  `;

  const content = (
    <>
      <span className="hidden sm:inline">Read More</span>
      <span className="sm:hidden">More</span>
      <UilArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
    </>
  );

  return isExternal ? (
    <a href={url} target="_blank" rel="noopener noreferrer" className={linkClasses}>
      {content}
    </a>
  ) : (
    <Link to={url} className={linkClasses}>
      {content}
    </Link>
  );
};

/**
 * NewsItem Component
 * Composition - combines smaller components
 */
const NewsItem = ({ item }) => {
  const itemUrl = getItemUrl(item);
  const isExternal = isExternalUrl(itemUrl);
  const imageUrl = getImageUrl(item);

  const WrapperComponent = useCallback(({ children }) => 
    isExternal ? (
      <a href={itemUrl} target="_blank" rel="noopener noreferrer" className="block">
        {children}
      </a>
    ) : (
      <Link to={itemUrl} className="block">{children}</Link>
    ), [itemUrl, isExternal]
  );

  return (
    <div className="group p-3 sm:p-4 md:p-5 lg:p-6 hover:bg-gray-50 transition-all duration-300">
      <WrapperComponent>
        <div className="flex items-start gap-3 sm:gap-4 md:gap-5 lg:gap-6">
          <NewsItemImage src={imageUrl} alt={item.title} />
          <NewsItemContent 
            title={item.title}
            description={item.description}
            date={item.createdAt || item.publish_date}
          />
        </div>
      </WrapperComponent>
      <div className="flex justify-end mt-2 sm:mt-3">
        <ReadMoreLink url={itemUrl} isExternal={isExternal} />
      </div>
    </div>
  );
};

/**
 * LatestNewsWidget Component
 * Main component following Composition pattern
 */
const LatestNewsWidget = () => {
  const dispatch = useDispatch();
  const [newsItems, setNewsItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getPostByType("news")).then((response) => {
      if (response.payload?.length > 0) {
        setNewsItems(response.payload.slice(0, CONFIG.MAX_NEWS_ITEMS));
      }
      setIsLoading(false);
    });
  }, [dispatch]);

  return (
    <div className="w-full bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
      {/* Header */}
      <header className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 sm:p-5 md:p-6">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white text-center">
          Recent Activities
        </h2>
      </header>

      {/* Content */}
      <main>
        {isLoading ? (
          <LoadingSpinner />
        ) : newsItems.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {newsItems.map((item) => (
              <NewsItem key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No recent activities found
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="p-3 sm:p-4 md:p-6 bg-gray-50 border-t border-gray-100">
        <Link
          to="/news-events"
          className="w-full flex items-center justify-center 
                     bg-white shadow-sm hover:shadow-md 
                     py-2 sm:py-3 px-4 sm:px-6 
                     text-gray-700 hover:text-indigo-600 
                     font-medium text-sm sm:text-base 
                     rounded-lg transition-all duration-300
                     border border-gray-200 hover:border-indigo-200"
        >
          <span>View All Activities</span>
          <UilArrowRight size={18} className="ml-2" />
        </Link>
      </footer>
    </div>
  );
};

export default LatestNewsWidget;